// ============================================================
// Helper utilities (inlined from Fuel Made modules/helpers)
// ============================================================

const fmTsClasses = { active: 'is-active', visible: 'is-visible' }

const fmTsDelay = (timeout) =>
  new Promise(done => setTimeout(done, timeout || 300))

const fmTsUpdateAttribute = (element, attribute, value, set = true) => {
  if (set === false) element.removeAttribute(attribute)
  else element.setAttribute(attribute, value)
}

const fmTsTransitionVisibility = async (element, show = true, timeout = 300, toggleAriaHidden = false) => {
  if (!element || element.isTransitioning) return
  element.isTransitioning = true
  if (show) {
    if (toggleAriaHidden) fmTsUpdateAttribute(element, 'aria-hidden', undefined, false)
    element.classList.add(fmTsClasses.active)
    element.offsetHeight // force reflow
    await new Promise(requestAnimationFrame)
    element.classList.add(fmTsClasses.visible)
  }
  if (!show) {
    element.classList.remove(fmTsClasses.visible)
    await fmTsDelay(timeout)
    element.classList.remove(fmTsClasses.active)
    if (toggleAriaHidden) fmTsUpdateAttribute(element, 'aria-hidden', 'true')
  }
  element.isTransitioning = false
}

const fmTsGenerateId = () =>
  typeof crypto?.randomUUID === 'function'
    ? crypto.randomUUID()
    : Math.random().toString(36).substring(2, 15)

// ============================================================
// FmTabList Custom Element (adapted from Fuel Made Tabs class)
// ============================================================

class FmTabList extends HTMLElement {
  constructor() {
    super()
    this.selectors = { tab: "[role='tab']", tablist: "[role='tablist']" }
    this.content = { container: this, tablist: [], buttons: [] }
    this.classes = { active: 'is-active' }
    this.data = {
      breakpoint: this.getAttribute('data-multiselectable'),
      multiselect: false,
      mediaQuery: false
    }
    if (this.data.breakpoint) {
      this.data.mediaQuery = window.matchMedia(this.data.breakpoint) || false
    }
    this._splides = {}
    this.init()
  }

  // ---- Content discovery ----

  _buildButtonsFromPanels() {
    const tabBtnsEl = this.querySelector('.fm-ts__tab-btns')
    if (!tabBtnsEl) return
    const panels = [...this.querySelectorAll('.fm-ts__panel')]
      .filter(p => p.closest('fm-tab-list') === this)
    while (tabBtnsEl.firstChild) tabBtnsEl.removeChild(tabBtnsEl.firstChild)
    for (const panel of panels) {
      const panelId = panel.id
      const iconUrl = panel.dataset.tabIcon
      const title = panel.dataset.tabTitle
      const subtitle = panel.dataset.tabSubtitle
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'fm-ts__tab-btn'
      btn.setAttribute('role', 'tab')
      btn.setAttribute('id', panelId.replace(/^panel-/, 'tab-btn-'))
      btn.setAttribute('aria-controls', panelId)
      btn.setAttribute('aria-selected', 'false')
      if (iconUrl) {
        const icon = document.createElement('span')
        icon.className = 'fm-ts__tab-icon'
        icon.style.setProperty('--tab-icon-url', `url('${iconUrl}')`)
        icon.setAttribute('aria-hidden', 'true')
        btn.appendChild(icon)
      }
      const texts = document.createElement('span')
      texts.className = 'fm-ts__tab-texts'
      if (title) {
        const titleEl = document.createElement('span')
        titleEl.className = 'fm-ts__tab-title'
        titleEl.textContent = title
        texts.appendChild(titleEl)
      }
      if (subtitle) {
        const subtitleEl = document.createElement('span')
        subtitleEl.className = 'fm-ts__tab-subtitle'
        subtitleEl.textContent = subtitle
        texts.appendChild(subtitleEl)
      }
      btn.appendChild(texts)
      tabBtnsEl.appendChild(btn)
    }
  }

  _getContent() {
    this._buildButtonsFromPanels()
    this.content.tablist = [...this.querySelectorAll(this.selectors.tablist)]
      .filter(tl => tl.closest('fm-tab-list') === this)[0]
    this.content.buttons = [...this.querySelectorAll(this.selectors.tab)]
      .filter(btn => btn.closest('fm-tab-list') === this)
  }

  _checkTablistOwnership() {
    if (!this.content.tablist || this.content.tablist.children.length > 0) return
    this.content.tablist.setAttribute(
      'aria-owns',
      this.content.buttons.map(b => b.getAttribute('id')).join(' ')
    )
  }

  _ensureSelectedTab() {
    const selected = this.content.buttons.find(b => b.getAttribute('aria-selected') === 'true')
    if (selected) return
    window.requestAnimationFrame(() => this.content.buttons[0]?.click())
  }

  // ---- Multiselect ----

  _adjustMultiselect(isEnabled = undefined) {
    const enable = isEnabled ?? (this.data.breakpoint && this.data.mediaQuery && this.data.mediaQuery.matches)
    this.data.multiselect = enable
    if (this.content.tablist) {
      enable
        ? this.content.tablist.setAttribute('aria-multiselectable', 'true')
        : this.content.tablist.removeAttribute('aria-multiselectable')
    }
    this._adjustAllTabindex(enable)
  }

  _changeMultiselect(e) {
    this._adjustMultiselect(e.matches)
    if (!e.matches) this._adjustAllBtns()
  }

  _adjustAllTabindex(enableMultiselect) {
    for (const button of this.content.buttons) {
      if (enableMultiselect) {
        button.removeAttribute('tabindex')
      } else {
        if (button.getAttribute('aria-selected') === 'false') {
          button.setAttribute('tabindex', '-1')
        }
      }
    }
  }

  // ---- Button/panel state ----

  _adjustTabBtn(button, selected) {
    button.setAttribute('aria-selected', String(selected))
    button.classList.toggle(this.classes.active, selected)
    if (!this.data.multiselect) {
      selected ? button.removeAttribute('tabindex') : button.setAttribute('tabindex', '-1')
    } else {
      button.removeAttribute('tabindex')
    }
  }

  async _adjustAllBtns(target = this.content.buttons[0]) {
    for (const button of this.content.buttons) {
      const isSamePanel = button !== target &&
        button.getAttribute('aria-controls') === target.getAttribute('aria-controls')
      if (isSamePanel) {
        this._adjustTabBtn(button, true)
      } else if (button !== target && !this.data.multiselect) {
        this._handleNonClickedBtn(button)
      } else if (button === target) {
        this._handleClickedBtn(button)
      }
    }
  }

  _handleNonClickedBtn(button) {
    this._adjustTabBtn(button, false)
    this._handlePanelVisibility(button)
  }

  _handleClickedBtn(button) {
    const isSelected = button.getAttribute('aria-selected') === 'true'
    const isAdjustable = (isSelected && this.data.multiselect) || !isSelected
    if (!isAdjustable) return
    this._adjustTabBtn(button, !isSelected)
    this._handlePanelVisibility(button)
  }

  async _handlePanelVisibility(button) {
    const selected = button.getAttribute('aria-selected') === 'true'
    const panelID = button.getAttribute('aria-controls')
    const panel = document.getElementById(panelID)
    if (!panel) return

    await fmTsTransitionVisibility(panel, selected, 250, true)

    // Init Splide on first open
    if (selected) this._initSplide(panel)
  }

  // ---- Splide ----

  _initSplide(panel) {
    if (!window.Splide) {
      if (document.contains(panel)) requestAnimationFrame(() => this._initSplide(panel))
      return
    }
    const splideEl = panel.querySelector('.splide')
    if (!splideEl) return
    const key = splideEl.id
    if (this._splides[key]) {
      this._splides[key].refresh()
      return
    }
    const configEl = panel.querySelector('.fm-ts__splide-config')
    const config = configEl ? JSON.parse(configEl.textContent) : {}
    const splide = new window.Splide(splideEl, config)
    this._bindControls(splide, panel)
    splide.mount()
    this._splides[key] = splide
  }

  _bindControls(splide, panel) {
    const dotsEl = panel.querySelector('.fm-slideshow__dots')
    const prevBtn = panel.querySelector('.fm-slideshow__prev')
    const nextBtn = panel.querySelector('.fm-slideshow__next')

    const buildDots = () => {
      if (!dotsEl) return
      while (dotsEl.firstChild) dotsEl.removeChild(dotsEl.firstChild)
      for (let i = 0; i < splide.length; i++) {
        const dot = document.createElement('button')
        dot.type = 'button'
        dot.className = 'fm-slideshow__dot'
        dot.setAttribute('aria-label', `Slide ${i + 1}`)
        dot.classList.toggle('is-active', i === splide.index)
        dot.addEventListener('click', () => splide.go(i))
        dotsEl.appendChild(dot)
      }
    }

    splide.on('mounted move', buildDots)
    prevBtn?.addEventListener('click', () => splide.go('<'))
    nextBtn?.addEventListener('click', () => splide.go('>'))
  }

  // ---- Events ----

  _handleEvent(e) {
    const parentTab = e.target.closest(this.selectors.tab)
    if (!parentTab) return
    if (e.type === 'click') this._handleClick(e, parentTab)
    if (e.type === 'keydown') this._handleKeydown(e, parentTab)
  }

  _handleClick(e, parentTab) {
    this._adjustAllBtns(parentTab)
  }

  _handleKeydown(e, parentTab) {
    if (parentTab !== e.target) return
    const key = e.key
    const currentIndex = this.content.buttons.indexOf(parentTab)
    const last = this.content.buttons.length - 1
    let newIndex = false
    if (key === 'ArrowUp' || key === 'ArrowLeft') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : last
    }
    if (key === 'ArrowDown' || key === 'ArrowRight') {
      newIndex = currentIndex >= last ? 0 : currentIndex + 1
    }
    if (key === 'Home') newIndex = 0
    if (key === 'End') newIndex = last
    if (newIndex !== false) {
      e.preventDefault()
      this.content.buttons[newIndex].focus()
    }
  }

  _attachEventListeners() {
    this.content.container.addEventListener('click', this._handleEvent.bind(this))
    this.content.container.addEventListener('keydown', this._handleEvent.bind(this))
    if (this.data.mediaQuery) {
      this.data.mediaQuery.addEventListener('change', this._changeMultiselect.bind(this))
    }
  }

  // ---- Init ----

  _preInitSplides(callback) {
    if (!window.Splide) {
      requestAnimationFrame(() => this._preInitSplides(callback))
      return
    }
    const panels = [...this.querySelectorAll('.fm-ts__panel')]
      .filter(p => p.closest('fm-tab-list') === this)
    for (const panel of panels) {
      this._initSplide(panel)
    }
    callback?.()
  }

  init() {
    if (!this.hasAttribute('id')) this.setAttribute('id', fmTsGenerateId())
    this._getContent()
    if (!this.content.tablist) {
      console.warn('FmTabList: no [role="tablist"] found')
      return
    }
    this._checkTablistOwnership()
    this._adjustMultiselect()
    this._attachEventListeners()
    this._preInitSplides(() => this._ensureSelectedTab())
  }
}

if (customElements.get('fm-tab-list') === undefined) {
  customElements.define('fm-tab-list', FmTabList)
}
